import { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowDown } from 'lucide-react';
import { useConversationStore } from '../../stores/conversationStore';
import { ScrollArea } from '../ui/scroll-area';
import { Message } from './Message';
import { StreamingMessage } from './StreamingMessage';

export function MessageList() {
  const { activeConversationId, messages, isLoading, streamingMessage } =
    useConversationStore();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [wasStreaming, setWasStreaming] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Check if scrolled to bottom
  const checkScrollPosition = useCallback(() => {
    const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      const { scrollTop, scrollHeight, clientHeight } = scrollArea;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isAtBottom);
    }
  }, []);

  // Scroll to bottom handler
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const currentMessages = activeConversationId
    ? messages[activeConversationId] || []
    : [];

  const showStreamingMessage =
    streamingMessage?.conversationId === activeConversationId &&
    streamingMessage.isStreaming;

  // Find the last user message index
  const lastUserMessageIndex = currentMessages.reduceRight(
    (found, msg, index) => (found === -1 && msg.role === 'user' ? index : found),
    -1
  );

  // When streaming starts, scroll to keep the user's question at the top
  useEffect(() => {
    if (showStreamingMessage && !wasStreaming) {
      // Streaming just started - scroll to last user message
      lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setWasStreaming(true);
    } else if (!showStreamingMessage && wasStreaming) {
      // Streaming just ended
      setWasStreaming(false);
    }
  }, [showStreamingMessage, wasStreaming]);

  // Scroll to bottom when new complete messages arrive (not during streaming)
  useEffect(() => {
    if (!showStreamingMessage) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentMessages.length, showStreamingMessage]);

  // Reset scroll button when conversation changes
  useEffect(() => {
    setShowScrollButton(false);
  }, [activeConversationId]);

  // Set up scroll event listener
  useEffect(() => {
    // Small delay to ensure ScrollArea is rendered
    const timeoutId = setTimeout(() => {
      const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.addEventListener('scroll', checkScrollPosition);
        // Initial check
        checkScrollPosition();
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const scrollArea = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.removeEventListener('scroll', checkScrollPosition);
      }
    };
  }, [checkScrollPosition, activeConversationId, currentMessages.length]);

  if (!activeConversationId) {
    return (
      <div className="flex items-center justify-center h-full text-center p-8">
        <div>
          <h2 className="text-2xl font-semibold mb-2">Willkommen!</h2>
          <p className="text-muted-foreground">
            Wählen Sie einen Chat aus oder erstellen Sie einen neuen Chat, um zu
            beginnen.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading && currentMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Nachrichten werden geladen...</p>
        </div>
      </div>
    );
  }

  if (currentMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-center p-8">
        <div>
          <h3 className="text-lg font-semibold mb-2">Leerer Chat</h3>
          <p className="text-muted-foreground">
            Stellen Sie eine Frage, um das Gespräch zu beginnen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full" ref={scrollAreaRef}>
        <ScrollArea className="h-full">
          <div className="flex flex-col max-w-3xl mx-auto w-full px-2 md:px-4 py-4 space-y-4">
            {currentMessages.map((message, index) => (
              <div
                key={message.id}
                ref={index === lastUserMessageIndex ? lastUserMessageRef : undefined}
              >
                <Message message={message} />
              </div>
            ))}
            {showStreamingMessage && (
              <StreamingMessage content={streamingMessage.content} />
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Scroll to bottom button - centered in chat area above input */}
      {showScrollButton && (
        <button
          className="fixed bottom-32 left-1/2 md:left-[calc(50%+9.5rem)] -translate-x-1/2 h-9 w-9 rounded-full bg-muted/60 text-muted-foreground shadow-md z-50 flex items-center justify-center hover:bg-muted transition-colors border border-border"
          onClick={scrollToBottom}
          aria-label="Nach unten scrollen"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      )}
    </>
  );
}
